import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Package,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { getPendingExchangeItemsThunk } from "../../../store/thunks/adminCartThunks";
import {
  setExchangeFilters,
  clearExchangeFilters,
} from "../../../store/slices/adminCartSlice";
import LoadingSpinner from "../../../components/common/Loader";

const ExchangeItemsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    pendingExchangeItems,
    exchangePagination,
    exchangeFilters,
    loadingExchange,
    error,
    itemTypes,
  } = useSelector((state) => state.cart);

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchExchangeItems();
  }, [exchangeFilters, exchangePagination.currentPage]);

  const fetchExchangeItems = () => {
    dispatch(
      getPendingExchangeItemsThunk({
        ...exchangeFilters,
        page: exchangePagination.currentPage,
        limit: exchangePagination.pageSize,
      })
    );
  };

  const handleViewDetails = (cartId, itemId) => {
    navigate(`/cart/exchange-items/${cartId}/${itemId}`);
  };

  const handleFilterChange = (key, value) => {
    dispatch(setExchangeFilters({ [key]: value }));
  };

  const handlePageChange = (page) => {
    dispatch(setExchangeFilters({ page }));
  };

  const getConditionBadge = (condition) => {
    const colors = {
      EXCELLENT: "bg-green-100 text-green-800",
      GOOD: "bg-blue-100 text-blue-800",
      FAIR: "bg-yellow-100 text-yellow-800",
      POOR: "bg-red-100 text-red-800",
    };
    return colors[condition] || "bg-gray-100 text-gray-800";
  };

  if (loadingExchange && pendingExchangeItems.length === 0)
    return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Exchange Items</h1>
          <p className="text-gray-600 mt-1">
            Verify and approve customer exchange items
          </p>
        </div>
        <button
          onClick={fetchExchangeItems}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Item Type Filter */}
          <select
            value={exchangeFilters.itemType}
            onChange={(e) => handleFilterChange("itemType", e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            {itemTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>

          {/* Sort Options */}
          <select
            value={`${exchangeFilters.sortBy}-${exchangeFilters.sortOrder}`}
            onChange={(e) => {
              const [sortBy, sortOrder] = e.target.value.split("-");
              handleFilterChange("sortBy", sortBy);
              handleFilterChange("sortOrder", sortOrder);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="addedAt-desc">Newest First</option>
            <option value="addedAt-asc">Oldest First</option>
            <option value="grossWeight-desc">Highest Weight</option>
            <option value="grossWeight-asc">Lowest Weight</option>
          </select>

          <button
            onClick={() => dispatch(clearExchangeFilters())}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg">
          {error}
        </div>
      )}

      {/* Exchange Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {pendingExchangeItems.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No pending exchange items</p>
          </div>
        ) : (
          pendingExchangeItems.map((item) => (
            <div
              key={item._id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Item Images */}
              {item.images?.length > 0 && (
                <div className="h-48 bg-gray-100 relative">
                  <img
                    src={item.images[0]}
                    alt={item.description}
                    className="w-full h-full object-cover"
                  />
                  {item.images.length > 1 && (
                    <span className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                      +{item.images.length - 1} more
                    </span>
                  )}
                </div>
              )}

              {/* Item Details */}
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium text-gray-900 flex-1">
                    {item.description}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-xs text-yellow-600 ml-2">
                    <Clock className="w-3 h-3" />
                    Pending
                  </span>
                </div>

                {/* Metal Details */}
                <div className="space-y-2 text-sm text-gray-600 mb-3">
                  <div className="flex justify-between">
                    <span>Type:</span>
                    <span className="font-medium text-gray-900">
                      {item.itemType}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Purity:</span>
                    <span className="font-medium text-gray-900">
                      {item.metalDetails?.purity}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Gross Weight:</span>
                    <span className="font-medium text-gray-900">
                      {item.grossWeight}g
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Net Weight:</span>
                    <span className="font-medium text-gray-900">
                      {item.netWeight}g
                    </span>
                  </div>
                  {item.stoneWeight > 0 && (
                    <div className="flex justify-between">
                      <span>Stone Weight:</span>
                      <span className="font-medium text-gray-900">
                        {item.stoneWeight}g
                      </span>
                    </div>
                  )}
                </div>

                {/* Condition */}
                <div className="mb-3">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getConditionBadge(
                      item.condition
                    )}`}
                  >
                    {item.condition}
                  </span>
                  {item.isHallmarked && (
                    <span className="ml-2 inline-flex px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                      Hallmarked
                    </span>
                  )}
                </div>

                {/* Customer Info */}
                <div className="border-t pt-3 mb-3">
                  <p className="text-sm font-medium text-gray-900">
                    {item.user?.name}
                  </p>
                  <p className="text-xs text-gray-600">{item.user?.email}</p>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => handleViewDetails(item.cartId, item._id)}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  Review & Verify
                </button>

                {/* Added Time */}
                <p className="text-xs text-gray-500 text-center mt-2">
                  Added {new Date(item.addedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {exchangePagination.totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 flex justify-between items-center">
          <div className="text-sm text-gray-700">
            Showing{" "}
            {(exchangePagination.currentPage - 1) *
              exchangePagination.pageSize +
              1}{" "}
            to{" "}
            {Math.min(
              exchangePagination.currentPage * exchangePagination.pageSize,
              exchangePagination.totalCount
            )}{" "}
            of {exchangePagination.totalCount} items
          </div>
          <div className="flex gap-2">
            <button
              onClick={() =>
                handlePageChange(exchangePagination.currentPage - 1)
              }
              disabled={exchangePagination.currentPage === 1}
              className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() =>
                handlePageChange(exchangePagination.currentPage + 1)
              }
              disabled={
                exchangePagination.currentPage === exchangePagination.totalPages
              }
              className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExchangeItemsList;
